import { useCallback, useRef, useState } from "react";
import { Camera, Loader2, RotateCcw, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Eyebrow, Rule } from "@/components/editorial";
import { useEntitlements, usePaywall } from "@/features/paywall";
import { ApiError } from "@/lib/api";
import { env } from "@/lib/env";
import { getSupabase, isSupabaseConfigured } from "@/lib/supabase";
import { cn } from "@/lib/utils";
import type { CookingStep } from "@/types/menu";

// ── Types ──────────────────────────────────────────────────────────────

interface AnalyzedIngredient {
  name: string;
  quantity: number;
  unit: string;
  aisle: string;
}

interface AnalyzedRecipe {
  slug: string;
  title: string;
  summary: string | null;
  servings: number;
  prep_minutes: number;
  cook_minutes: number;
  ingredients: AnalyzedIngredient[];
  /** Steps extracted by the capture agent (may be empty). */
  prep_steps?: CookingStep[];
}

interface FromImageResponse {
  recipe: AnalyzedRecipe;
  notice: string;
}

// ── Component ──────────────────────────────────────────────────────────

type CaptureState =
  | { phase: "idle" }
  | { phase: "uploading" }
  | { phase: "analyzing" }
  | { phase: "preview"; result: FromImageResponse }
  | { phase: "saving" }
  | { phase: "saved" }
  | { phase: "error"; message: string };

export function RestaurantCapture() {
  const { t } = useTranslation();
  const { isPremium } = useEntitlements();
  const paywall = usePaywall();
  const [state, setState] = useState<CaptureState>({ phase: "idle" });
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadFile = useCallback(
    async (file: File): Promise<FromImageResponse> => {
      const formData = new FormData();
      formData.append("file", file);

      // Attach auth token for the backend
      const authHeader: Record<string, string> = {};
      if (isSupabaseConfigured()) {
        const {
          data: { session },
        } = await getSupabase().auth.getSession();
        if (session?.access_token) {
          authHeader["Authorization"] = `Bearer ${session.access_token}`;
        }
      }

      const res = await fetch(
        `${env.API_BASE_URL}/api/v1/favorites/recipe-book/from-image`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            ...authHeader,
            // Do NOT set Content-Type — fetch auto-sets multipart boundary
          },
          body: formData,
        },
      );

      if (!res.ok) {
        let parsed: unknown;
        try {
          parsed = await res.json();
        } catch {
          parsed = await res.text();
        }
        throw new ApiError(res.status, parsed);
      }

      return (await res.json()) as FromImageResponse;
    },
    [],
  );

  const saveRecipe = useCallback(
    async (recipe: AnalyzedRecipe): Promise<void> => {
      const body = {
        recipe_id: recipe.slug,
        title: recipe.title,
        summary: recipe.summary,
        ingredients: recipe.ingredients.map((i) => ({
          name: i.name,
          quantity: i.quantity,
          unit: i.unit,
          aisle: i.aisle,
        })),
        // Keep the captured steps: they used to be dropped on save.
        steps: recipe.prep_steps ?? [],
        servings: recipe.servings,
        prep_minutes: recipe.prep_minutes,
        cook_minutes: recipe.cook_minutes,
      };

      // Attach auth token
      const authHeader: Record<string, string> = {};
      if (isSupabaseConfigured()) {
        const {
          data: { session },
        } = await getSupabase().auth.getSession();
        if (session?.access_token) {
          authHeader["Authorization"] = `Bearer ${session.access_token}`;
        }
      }

      const res = await fetch(
        `${env.API_BASE_URL}/api/v1/favorites/recipe-book/from-image/save`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            ...authHeader,
          },
          body: JSON.stringify(body),
        },
      );

      if (!res.ok) {
        let parsed: unknown;
        try {
          parsed = await res.json();
        } catch {
          parsed = await res.text();
        }
        throw new ApiError(res.status, parsed);
      }
    },
    [],
  );
  const handleFilePicked = useCallback(
    async (file: File | null) => {
      if (!file) return;

      // Check premium
      if (!isPremium) {
        paywall.open({
          feature:
            "Capturez une recette depuis une photo — une fonctionnalité Le Cahier.",
        });
        return;
      }

      // Validate file type
      const allowed = ["image/jpeg", "image/png", "image/webp"];
      if (!allowed.includes(file.type)) {
        setState({
          phase: "error",
          message: "Format non supporté. Utilisez JPEG, PNG ou WebP.",
        });
        return;
      }

      // Validate file size (10 MB max)
      if (file.size > 10 * 1024 * 1024) {
        setState({
          phase: "error",
          message: "Image trop volumineuse. Taille max : 10 Mo.",
        });
        return;
      }

      // Show local preview
      const objectUrl = URL.createObjectURL(file);
      setPreviewUrl(objectUrl);
      setState({ phase: "uploading" });

      try {
        // Upload and analyze
        setState({ phase: "analyzing" });
        const result = await uploadFile(file);

        setState({ phase: "preview", result });
      } catch (err) {
        if (err instanceof ApiError && err.status === 402) {
          // Premium required (race condition — user might have lost entitlements)
          paywall.open({
            feature:
              "Capturez une recette depuis une photo — une fonctionnalité Le Cahier.",
          });
          reset();
          return;
        }
        setState({
          phase: "error",
          message:
            err instanceof Error
              ? err.message
              : "Erreur lors de l'analyse de l'image.",
        });
      }
    },
    [isPremium, paywall, uploadFile],
  );

  const handleSave = useCallback(async () => {
    if (state.phase !== "preview") return;

    setState({ phase: "saving" });
    try {
      await saveRecipe(state.result.recipe);
      setState({ phase: "saved" });
    } catch (err) {
      setState({
        phase: "error",
        message:
          err instanceof Error
            ? err.message
            : "Erreur lors de l'enregistrement.",
      });
    }
  }, [state, saveRecipe]);

  const reset = useCallback(() => {
    setState({ phase: "idle" });
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [previewUrl]);

  // Cleanup preview URL on unmount
  const hasRevoked = useRef(false);
  if (previewUrl && !hasRevoked.current) {
    hasRevoked.current = true;
  }

  // ── Render ───────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        capture="environment"
        className="hidden"
        onChange={(e) => handleFilePicked(e.target.files?.[0] ?? null)}
      />

      {/* State: Idle — Upload tile */}
      {state.phase === "idle" && (
        <button
          onClick={() => fileInputRef.current?.click()}
          className={cn(
            "flex items-start gap-4 border border-dashed border-hair bg-background p-4",
            "transition-colors hover:border-foreground hover:bg-bone/30",
            "w-full text-left",
          )}
        >
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-accent/10">
            <Camera className="size-5 text-accent" strokeWidth={1.5} />
          </div>
          <div className="min-w-0 flex-1">
            <span className="font-serif text-[16px] leading-[1.15]">
              Capturer une recette
            </span>
            <p className="mt-1 text-[12.5px] leading-snug text-ink-50">
              Prenez une photo d'un plat, d'une carte ou d'une liste de courses
              pour générer une recette.
            </p>
          </div>
        </button>
      )}

      {/* State: Uploading / Analyzing */}
      {(state.phase === "uploading" || state.phase === "analyzing") && (
        <div className="flex flex-col items-center gap-3 border border-hair bg-background p-8 text-center">
          {previewUrl && (
            <img
              src={previewUrl}
              alt="Aperçu"
              className="mb-2 h-40 w-full rounded object-cover"
            />
          )}
          <Loader2 className="size-6 animate-spin text-accent" />
          <p className="text-[13px] text-ink-50">
            {state.phase === "uploading"
              ? "Envoi de l'image…"
              : "Analyse par IA en cours…"}
          </p>
        </div>
      )}

      {/* State: Preview — Show AI-generated recipe */}
      {state.phase === "preview" && (
        <div className="border border-hair bg-background">
          {/* Image preview */}
          {previewUrl && (
            <img
              src={previewUrl}
              alt="Photo"
              className="h-48 w-full object-cover"
            />
          )}

          <div className="p-4">
            {/* Mandatory disclaimer banner */}
            <div className="mb-4 rounded bg-amber-50/80 px-3 py-2.5 text-[11px] leading-snug text-amber-800">
              <p className="font-medium uppercase tracking-wider">
                ⚠ Avis important
              </p>
              <p className="mt-1">{state.result.notice}</p>
            </div>

            {/* Recipe preview */}
            <h3 className="font-serif text-[18px] leading-[1.1]">
              {state.result.recipe.title}
            </h3>
            {state.result.recipe.summary && (
              <p className="mt-2 text-[13px] leading-snug text-ink-50">
                {state.result.recipe.summary}
              </p>
            )}

            {/* Timings */}
            <div className="mt-3 flex gap-4 text-[12px] text-ink-50">
              <span>
                Préparation :{" "}
                <strong className="text-foreground">
                  {state.result.recipe.prep_minutes} min
                </strong>
              </span>
              <span>
                Cuisson :{" "}
                <strong className="text-foreground">
                  {state.result.recipe.cook_minutes} min
                </strong>
              </span>
            </div>

            {/* Ingredients */}
            <Rule className="my-3" />
            <Eyebrow>Ingrédients estimés</Eyebrow>
            <ul className="mt-2 space-y-1">
              {state.result.recipe.ingredients.map((ing, i) => (
                <li
                  key={i}
                  className="flex items-center justify-between text-[13px]"
                >
                  <span>{ing.name}</span>
                  <span className="text-ink-50">
                    {ing.quantity} {ing.unit}
                  </span>
                </li>
              ))}
            </ul>

            {/* Steps */}
            {(state.result.recipe.prep_steps?.length ?? 0) > 0 && (
              <>
                <Rule className="my-3" />
                <Eyebrow>{t("recipe.steps")}</Eyebrow>
                <ol className="mt-2 space-y-1.5">
                  {state.result.recipe.prep_steps?.map((step) => (
                    <li key={step.order} className="text-[13px] leading-snug">
                      <span className="mr-1.5 font-semibold">{step.verb}</span>
                      {step.target}
                    </li>
                  ))}
                </ol>
              </>
            )}

            {/* Actions */}
            <div className="mt-5 flex gap-3">
              <Button
                variant="default"
                size="default"
                className="flex-1"
                onClick={handleSave}
              >
                Approuver et enregistrer
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={reset}
                aria-label="Annuler"
              >
                <X className="size-4" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* State: Saving */}
      {state.phase === "saving" && (
        <div className="flex flex-col items-center gap-3 border border-hair bg-background p-8 text-center">
          <Loader2 className="size-6 animate-spin text-accent" />
          <p className="text-[13px] text-ink-50">Enregistrement…</p>
        </div>
      )}

      {/* State: Saved */}
      {state.phase === "saved" && (
        <div className="border border-hair bg-background p-6 text-center">
          <p className="font-serif text-[16px]">
            ✓ Recette enregistrée dans votre cahier
          </p>
          <Button variant="outline" size="sm" className="mt-4" onClick={reset}>
            <Camera className="size-3.5" />
            Nouvelle capture
          </Button>
        </div>
      )}

      {/* State: Error */}
      {state.phase === "error" && (
        <div className="border border-hair bg-background p-6 text-center">
          <p className="text-[13px] text-red-600">{state.message}</p>
          <Button variant="outline" size="sm" className="mt-4" onClick={reset}>
            <RotateCcw className="size-3.5" />
            Réessayer
          </Button>
        </div>
      )}
    </div>
  );
}
